import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { User, IdCard, Hash, PenLine, Loader2, Briefcase, CalendarDays, Phone, Mail, Pencil, MessageCircle, CheckCircle2, AlertCircle, XCircle, Edit3, UserSquare2, Droplet, Shield } from "lucide-react";
import { API, useAuth } from "../App";
import PhoneChangeDialog from "./PhoneChangeDialog";
import WhatsAppEditDialog from "./WhatsAppEditDialog";
import SignatureEditDialog from "./SignatureEditDialog";
import InstallAppButton from "./InstallAppButton";

const formatCPF = (raw) => {
  if (!raw) return "—";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length !== 11) return raw;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

const formatPhone = (raw) => {
  if (!raw) return "—";
  const digits = String(raw).replace(/\D/g, "");
  // Brazilian E.164: +55 + DDD(2) + 9 digits
  if (digits.length === 13 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

/**
 * Employee profile dialog. Opens when the user clicks their name in the header.
 * Shows full name, matrícula (chapa), CPF and saved signature.
 */
const ProfileDialog = ({ open, onOpenChange }) => {
  const { token, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  const fetchProfile = () => {
    if (!token) return;
    setLoading(true);
    return axios
      .get(`${API}/employee/profile`, { params: { token }, timeout: 10000 })
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/employee/profile`, { params: { token }, timeout: 10000 })
      .then((res) => {
        if (!cancelled) setProfile(res.data);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, token]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-lg p-4 sm:p-6" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <User className="w-5 h-5 text-slate-700" />
            Meu Perfil
          </DialogTitle>
          <DialogDescription className="text-xs">
            Informações cadastradas no sistema AVI Segurança.
          </DialogDescription>
        </DialogHeader>

        {loading || !profile ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-3 sm:space-y-4 pt-2 max-h-[70vh] overflow-y-auto pr-1"
          >
            {/* 3x4 Badge Photo — shown at the top as a badge-style avatar */}
            <div className="flex justify-center pb-1">
              {profile.has_photo_3x4 && profile.photo_3x4_base64 ? (
                <div className="relative">
                  <img
                    src={profile.photo_3x4_base64}
                    alt="Foto 3x4"
                    className="w-32 h-[168px] object-cover rounded-lg border-4 border-white shadow-lg ring-1 ring-slate-200"
                    data-testid="profile-photo-3x4-image"
                  />
                  <Badge
                    variant="outline"
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[10px] bg-white shadow-sm border-sky-200 text-sky-700"
                  >
                    Foto do Crachá
                  </Badge>
                </div>
              ) : (
                <div className="w-32 h-[168px] rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <UserSquare2 className="w-8 h-8" />
                  <span className="text-[11px] text-center px-2 leading-tight">
                    Foto 3x4<br />pendente
                  </span>
                </div>
              )}
            </div>

            <ProfileField
              icon={<User className="w-4 h-4 text-slate-500" />}
              label="Nome completo"
              value={profile.nome}
              testid="profile-nome"
            />
            <ProfileField
              icon={<Hash className="w-4 h-4 text-slate-500" />}
              label="Chapa (matrícula)"
              value={profile.matricula}
              mono
              testid="profile-matricula"
            />
            <ProfileField
              icon={<IdCard className="w-4 h-4 text-slate-500" />}
              label="CPF"
              value={formatCPF(profile.cpf)}
              mono
              testid="profile-cpf"
            />
            <ProfileField
              icon={<Shield className="w-4 h-4 text-slate-500" />}
              label="Registro DPF"
              value={profile.registro_dpf}
              mono
              testid="profile-registro-dpf"
            />
            <ProfileField
              icon={<Droplet className="w-4 h-4 text-red-500" />}
              label="Tipo Sanguíneo"
              value={profile.tipo_sanguineo}
              testid="profile-tipo-sanguineo"
            />
            <ProfileField
              icon={<Briefcase className="w-4 h-4 text-slate-500" />}
              label="Posto de serviço"
              value={profile.posto_servico}
              testid="profile-posto"
            />
            <ProfileField
              icon={<CalendarDays className="w-4 h-4 text-slate-500" />}
              label="Data de admissão"
              value={profile.data_admissao}
              mono
              testid="profile-admissao"
            />
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <ProfileField
                  icon={<Phone className="w-4 h-4 text-slate-500" />}
                  label="Telefone"
                  value={formatPhone(profile.telefone)}
                  mono
                  testid="profile-telefone"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-5 h-7 text-[11px] px-2 flex-shrink-0"
                onClick={() => setPhoneDialogOpen(true)}
                data-testid="profile-edit-phone-btn"
              >
                <Pencil className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Trocar</span>
              </Button>
            </div>
            <ProfileField
              icon={<Mail className="w-4 h-4 text-slate-500" />}
              label="E-mail"
              value={profile.email || "—"}
              testid="profile-email"
            />

            {/* WhatsApp row */}
            <WhatsappRow
              profile={profile}
              onEdit={() => setWhatsappDialogOpen(true)}
            />

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <PenLine className="w-4 h-4" />
                  <span>Assinatura</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] px-2"
                  onClick={() => setSignatureDialogOpen(true)}
                  data-testid="profile-edit-signature-btn"
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  {profile.has_signature ? "Refazer" : "Cadastrar"}
                </Button>
              </div>
              {profile.has_signature && profile.signature_base64 ? (
                <div className="rounded-md border border-slate-200 bg-white p-3 flex items-center justify-center">
                  <img
                    src={profile.signature_base64}
                    alt="Minha assinatura"
                    className="max-h-32 object-contain"
                    data-testid="profile-signature-image"
                  />
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500">
                  Nenhuma assinatura cadastrada. Toque em "Cadastrar" para
                  desenhar sua rúbrica.
                </div>
              )}
              {profile.signature_created_at && (
                <p className="text-xs text-slate-400 mt-2 text-right">
                  Cadastrada em{" "}
                  {new Date(profile.signature_created_at).toLocaleString("pt-BR")}
                </p>
              )}
            </div>

            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              Para alterar dados cadastrais, entre em contato com o
              administrador.
            </p>

            <InstallAppButton />

            <div className="flex justify-between items-center gap-2 pt-1">
              <Button
                variant="default"
                onClick={() => {
                  const url = `${API}/employee/badge.pdf?token=${encodeURIComponent(token)}&v=${Date.now()}`;
                  window.open(url, "_blank");
                }}
                data-testid="profile-download-badge-btn"
                className="bg-slate-900 hover:bg-slate-800"
              >
                <IdCard className="w-4 h-4 mr-2" />
                Baixar Crachá
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="profile-close-btn"
              >
                Fechar
              </Button>
            </div>
          </motion.div>
        )}
      </DialogContent>

      <PhoneChangeDialog
        open={phoneDialogOpen}
        onOpenChange={setPhoneDialogOpen}
        token={token}
        profile={profile}
        onChanged={fetchProfile}
      />

      <WhatsAppEditDialog
        open={whatsappDialogOpen}
        onOpenChange={setWhatsappDialogOpen}
        profile={profile}
        onChanged={async (newStatus) => {
          if (newStatus) updateUser({ whatsapp_status: newStatus });
          await fetchProfile();
        }}
      />

      <SignatureEditDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        hasExisting={!!profile?.has_signature}
        onSaved={fetchProfile}
      />
    </Dialog>
  );
};

const WhatsappRow = ({ profile, onEdit }) => {
  const status = profile?.whatsapp_status || "pending";
  const STATUS_META = {
    confirmed: {
      text: "Confirmado",
      cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
      Icon: CheckCircle2,
    },
    pending: {
      text: "Pendente",
      cls: "bg-amber-100 text-amber-800 border-amber-200",
      Icon: AlertCircle,
    },
    none: {
      text: "Sem WhatsApp",
      cls: "bg-slate-100 text-slate-700 border-slate-200",
      Icon: XCircle,
    },
  };
  const meta = STATUS_META[status] || STATUS_META.pending;

  const displayValue =
    status === "confirmed"
      ? formatPhone(profile?.whatsapp)
      : status === "none"
      ? "Não tenho WhatsApp"
      : "Aguardando confirmação";

  return (
    <div className="flex items-start gap-2 sm:gap-3">
      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-emerald-50 flex items-center justify-center flex-shrink-0">
        <MessageCircle className="w-4 h-4 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 leading-tight">
          WhatsApp
        </p>
        <p
          className="text-sm sm:text-base text-slate-900 font-mono truncate"
          data-testid="profile-whatsapp"
        >
          {displayValue}
        </p>
        <Badge
          variant="outline"
          className={`mt-1 text-[10px] ${meta.cls}`}
          data-testid="profile-whatsapp-status"
        >
          <meta.Icon className="w-3 h-3 mr-1" />
          {meta.text}
        </Badge>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="mt-5 h-7 text-[11px] px-2 flex-shrink-0"
        onClick={onEdit}
        data-testid="profile-edit-whatsapp-btn"
      >
        <Edit3 className="w-3 h-3 sm:mr-1" />
        <span className="hidden sm:inline">Editar</span>
      </Button>
    </div>
  );
};

const ProfileField = ({ icon, label, value, mono, testid }) => (
  <div className="flex items-start gap-2 sm:gap-3">
    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 leading-tight">
        {label}
      </p>
      <p
        className={`text-sm sm:text-base text-slate-900 ${mono ? "font-mono" : "font-medium"} truncate`}
        data-testid={testid}
      >
        {value || "—"}
      </p>
    </div>
  </div>
);

export default ProfileDialog;
